import React, { useState } from 'react';
import { 
  doc, 
  updateDoc 
} from "firebase/firestore";
import { db } from '../../firebase/firestore';
import { 
  X,
  CheckCircle,
  AlertCircle,
  Loader,
  BookOpen,
  GraduationCap,
  Plus,
  Trash,
  Users,
  Settings,
  Save,
  Calendar as CalendarIcon,
  ChevronDown,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const AccessManager = ({ 
  isOpen, 
  onClose, 
  user,
  availableTrainings,
  availableCourses,
  trainingsMap,
  coursesMap,
  onAccessUpdated
}) => {
  const [loading, setLoading] = useState(false);
  const [accessType, setAccessType] = useState('training');
  const [globalStartDate, setGlobalStartDate] = useState('');
  const [globalEndDate, setGlobalEndDate] = useState('');
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [editingMaterialType, setEditingMaterialType] = useState(null);
  const [editStartDate, setEditStartDate] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  
  // Состояние материалов пользователя
  const [userMaterials, setUserMaterials] = useState({
    trainings: [],
    courses: [],
    trainingsAccess: {},
    coursesAccess: {}
  });
  const [selectedMaterials, setSelectedMaterials] = useState({
    trainings: [],
    courses: []
  });

  // Уведомления
  const [notification, setNotification] = useState({ type: '', message: '' });

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification({ type: '', message: '' }), 5000);
  };

  // Форматирование даты
  const formatDateForInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getDaysBetween = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Получение названий
  const getTrainingTitle = (trainingId) => {
    return trainingsMap[trainingId] || trainingId.substring(0, 8) + '...';
  };

  const getCourseTitle = (courseId) => {
    return coursesMap[courseId] || courseId.substring(0, 8) + '...';
  };

  // Получение списков материалов пользователя
  const getUserTrainingsList = (userData) => {
    const trainingsFromArray = userData.trainings || [];
    const trainingsFromAccess = (userData.trainingsAccess || []).map(access => access.id);
    return [...new Set([...trainingsFromArray, ...trainingsFromAccess])];
  };

  const getUserCoursesList = (userData) => {
    const coursesFromArray = userData.courses || [];
    const coursesFromAccess = (userData.coursesAccess || []).map(access => access.id);
    return [...new Set([...coursesFromArray, ...coursesFromAccess])];
  };

  // Инициализация данных при открытии
  React.useEffect(() => {
    if (isOpen && user) {
      const userTrainings = getUserTrainingsList(user);
      const userCourses = getUserCoursesList(user);
      
      const trainingsAccessMap = {};
      (user.trainingsAccess || []).forEach(access => {
        trainingsAccessMap[access.id] = access;
      });
      
      const coursesAccessMap = {};
      (user.coursesAccess || []).forEach(access => {
        coursesAccessMap[access.id] = access;
      });
      
      setUserMaterials({
        trainings: userTrainings,
        courses: userCourses,
        trainingsAccess: trainingsAccessMap,
        coursesAccess: coursesAccessMap
      });
      
      setSelectedMaterials({
        trainings: [...userTrainings],
        courses: [...userCourses]
      });
      
      // Устанавливаем глобальные даты по умолчанию (30 дней)
      const now = new Date();
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + 30);
      setGlobalStartDate(formatDateForInput(now));
      setGlobalEndDate(formatDateForInput(endDate));
      
      setAccessType('training');
    }
  }, [isOpen, user]);

  // Добавление/удаление тренинга
  const toggleTraining = (trainingId) => {
    setSelectedMaterials(prev => ({
      ...prev,
      trainings: prev.trainings.includes(trainingId)
        ? prev.trainings.filter(id => id !== trainingId)
        : [...prev.trainings, trainingId]
    }));
  };

  // Добавление/удаление курса
  const toggleCourse = (courseId) => {
    setSelectedMaterials(prev => ({
      ...prev,
      courses: prev.courses.includes(courseId)
        ? prev.courses.filter(id => id !== courseId)
        : [...prev.courses, courseId]
    }));
  };

  // Открытие редактирования периода
  const startEditPeriod = (materialId, materialType) => {
    let accessData = null;
    if (materialType === 'training') {
      accessData = userMaterials.trainingsAccess[materialId];
    } else {
      accessData = userMaterials.coursesAccess[materialId];
    }
    
    setEditingMaterial(materialId);
    setEditingMaterialType(materialType);
    setEditStartDate(accessData?.startDate || globalStartDate);
    setEditEndDate(accessData?.endDate || globalEndDate);
  };

  // Сохранение периода для конкретного материала
  const saveMaterialPeriod = async () => {
    if (!user || !editingMaterial) return;
    
    if (!editStartDate || !editEndDate) {
      showNotification('error', 'Укажите даты начала и окончания');
      return;
    }
    
    const start = new Date(editStartDate);
    const end = new Date(editEndDate);
    
    if (end <= start) {
      showNotification('error', 'Дата окончания должна быть позже даты начала');
      return;
    }
    
    setLoading(true);
    
    try {
      const userRef = doc(db, "telegramUsers", user.id);
      let updateField = '';
      let updatedAccess = [];
      
      if (editingMaterialType === 'training') {
        updateField = 'trainingsAccess';
        updatedAccess = [...(user.trainingsAccess || [])];
        const existingIndex = updatedAccess.findIndex(a => a.id === editingMaterial);
        
        if (existingIndex >= 0) {
          updatedAccess[existingIndex] = {
            ...updatedAccess[existingIndex],
            startDate: editStartDate,
            endDate: editEndDate,
            updatedAt: new Date().toISOString()
          };
        } else {
          updatedAccess.push({
            id: editingMaterial,
            startDate: editStartDate,
            endDate: editEndDate,
            grantedAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          });
        }
      } else {
        updateField = 'coursesAccess';
        updatedAccess = [...(user.coursesAccess || [])];
        const existingIndex = updatedAccess.findIndex(a => a.id === editingMaterial);
        
        if (existingIndex >= 0) {
          updatedAccess[existingIndex] = {
            ...updatedAccess[existingIndex],
            startDate: editStartDate,
            endDate: editEndDate,
            updatedAt: new Date().toISOString()
          };
        } else {
          updatedAccess.push({
            id: editingMaterial,
            startDate: editStartDate,
            endDate: editEndDate,
            grantedAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          });
        }
      }
      
      await updateDoc(userRef, {
        [updateField]: updatedAccess,
        updatedAt: new Date().toISOString()
      });
      
      // Обновляем локальное состояние
      if (editingMaterialType === 'training') {
        setUserMaterials(prev => ({
          ...prev,
          trainingsAccess: {
            ...prev.trainingsAccess,
            [editingMaterial]: {
              id: editingMaterial,
              startDate: editStartDate,
              endDate: editEndDate,
              updatedAt: new Date().toISOString()
            }
          }
        }));
      } else {
        setUserMaterials(prev => ({
          ...prev,
          coursesAccess: {
            ...prev.coursesAccess,
            [editingMaterial]: {
              id: editingMaterial,
              startDate: editStartDate,
              endDate: editEndDate,
              updatedAt: new Date().toISOString()
            }
          }
        }));
      }
      
      showNotification('success', 'Период доступа обновлен');
      setEditingMaterial(null);
      setEditingMaterialType(null);
      
      // Уведомляем родительский компонент об обновлении
      if (onAccessUpdated) {
        onAccessUpdated();
      }
      
    } catch (error) {
      console.error("Error updating access period:", error);
      showNotification('error', `Ошибка: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

// Сохранение всех изменений
const saveAllChanges = async () => {
  if (!user) return;
  
  setLoading(true);
  
  try {
    // Определяем коллекцию в зависимости от типа пользователя
    const collectionName = user.userType === 'web' ? 'webUsers' : 'telegramUsers';
    const userRef = doc(db, collectionName, user.id);
    
    const newTrainingsAccess = [...(user.trainingsAccess || [])];
    const newCoursesAccess = [...(user.coursesAccess || [])];
    
    // Добавляем доступ для новых тренингов
    selectedMaterials.trainings.forEach(trainingId => {
      if (!userMaterials.trainings.includes(trainingId)) {
        const existing = newTrainingsAccess.find(a => a.id === trainingId);
        if (!existing) {
          newTrainingsAccess.push({
            id: trainingId,
            startDate: globalStartDate,
            endDate: globalEndDate,
            grantedAt: new Date().toISOString()
          });
        }
      }
    });
    
    // Удаляем доступ для удаленных тренингов
    userMaterials.trainings.forEach(trainingId => {
      if (!selectedMaterials.trainings.includes(trainingId)) {
        const index = newTrainingsAccess.findIndex(a => a.id === trainingId);
        if (index >= 0) {
          newTrainingsAccess.splice(index, 1);
        }
      }
    });
    
    // Добавляем доступ для новых курсов
    selectedMaterials.courses.forEach(courseId => {
      if (!userMaterials.courses.includes(courseId)) {
        const existing = newCoursesAccess.find(a => a.id === courseId);
        if (!existing) {
          newCoursesAccess.push({
            id: courseId,
            startDate: globalStartDate,
            endDate: globalEndDate,
            grantedAt: new Date().toISOString()
          });
        }
      }
    });
    
    // Удаляем доступ для удаленных курсов
    userMaterials.courses.forEach(courseId => {
      if (!selectedMaterials.courses.includes(courseId)) {
        const index = newCoursesAccess.findIndex(a => a.id === courseId);
        if (index >= 0) {
          newCoursesAccess.splice(index, 1);
        }
      }
    });
    
    await updateDoc(userRef, {
      trainings: selectedMaterials.trainings,
      courses: selectedMaterials.courses,
      trainingsAccess: newTrainingsAccess,
      coursesAccess: newCoursesAccess,
      updatedAt: new Date().toISOString()
    });
    
    showNotification('success', 'Доступ к материалам обновлен');
    
    // Уведомляем родительский компонент об обновлении
    if (onAccessUpdated) {
      onAccessUpdated();
    }
    
    onClose();
    
  } catch (error) {
    console.error("Error saving access:", error);
    showNotification('error', `Ошибка: ${error.message}`);
  } finally {
    setLoading(false);
  }
};

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
      <div className="bg-gray-900 rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden shadow-2xl border border-gray-700">
        {/* Уведомления внутри модалки */}
        {notification.message && (
          <div className={`absolute top-4 left-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg max-w-md ${
            notification.type === 'success' 
              ? 'bg-green-900/30 text-green-300 border border-green-700' 
              : notification.type === 'error'
              ? 'bg-red-900/30 text-red-300 border border-red-700'
              : 'bg-yellow-900/30 text-yellow-300 border border-yellow-700'
          }`}>
            {notification.type === 'success' ? (
              <CheckCircle className="w-5 h-5" />
            ) : notification.type === 'error' ? (
              <AlertCircle className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            <span className="text-sm font-medium">{notification.message}</span>
          </div>
        )}
        
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between bg-gradient-to-r from-gray-800 to-gray-900">
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-purple-400" />
            <h2 className="text-lg font-semibold text-white">Управление доступом к материалам</h2>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-300 hover:bg-gray-800 rounded transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="flex h-full max-h-[calc(90vh-70px)]">
          {/* Левая панель */}
          <div className="w-80 border-r border-gray-700 p-4 overflow-y-auto">
            <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-900/50 to-purple-900/50 flex items-center justify-center">
                  <Users className="w-6 h-6 text-gray-300" />
                </div>
                <div>
                  <p className="text-sm font-medium text-white">{user?.fullName || user?.username || user?.id?.substring(0, 8)}</p>
                  <p className="text-xs text-gray-400">@{user?.username || 'без username'}</p>
                  <p className="text-xs text-gray-500 mt-1">ID: {user?.id}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-purple-900/20 rounded-lg p-4 border border-purple-700/30 mb-4">
              <h3 className="text-sm font-medium text-purple-300 mb-3 flex items-center gap-2">
                <CalendarIcon className="w-4 h-4" />
                Период по умолчанию для новых материалов
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Дата начала</label>
                  <input
                    type="date"
                    value={globalStartDate}
                    onChange={(e) => setGlobalStartDate(e.target.value)}
                    min={formatDateForInput(new Date())}
                    className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Дата окончания</label>
                  <input
                    type="date"
                    value={globalEndDate}
                    onChange={(e) => setGlobalEndDate(e.target.value)}
                    min={globalStartDate}
                    className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                  />
                </div>
                {globalStartDate && globalEndDate && (
                  <div className="bg-purple-900/30 rounded-lg p-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-400">Длительность:</span>
                      <span className="text-purple-300 font-medium">{getDaysBetween(globalStartDate, globalEndDate)} дней</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="bg-green-900/20 rounded-lg p-4 border border-green-700/30">
              <h3 className="text-sm font-medium text-green-300 mb-3 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Статистика
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Тренинги:</span>
                  <span className="text-white font-medium">{selectedMaterials.trainings.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Курсы:</span>
                  <span className="text-white font-medium">{selectedMaterials.courses.length}</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-gray-700">
                  <span className="text-gray-400">Всего материалов:</span>
                  <span className="text-purple-300 font-bold">{selectedMaterials.trainings.length + selectedMaterials.courses.length}</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Правая панель */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="flex border-b border-gray-700 px-4">
              <button
                onClick={() => setAccessType('training')}
                className={`px-4 py-3 text-sm font-medium transition-colors relative ${
                  accessType === 'training'
                    ? 'text-purple-400 border-b-2 border-purple-400'
                    : 'text-gray-400 hover:text-gray-300'
                }`}
              >
                <BookOpen className="w-4 h-4 inline mr-2" />
                Тренинги
                <span className="ml-2 px-1.5 py-0.5 text-xs bg-gray-800 rounded-full">{availableTrainings.length}</span>
              </button>
              <button
                onClick={() => setAccessType('course')}
                className={`px-4 py-3 text-sm font-medium transition-colors relative ${
                  accessType === 'course'
                    ? 'text-green-400 border-b-2 border-green-400'
                    : 'text-gray-400 hover:text-gray-300'
                }`}
              >
                <GraduationCap className="w-4 h-4 inline mr-2" />
                Курсы
                <span className="ml-2 px-1.5 py-0.5 text-xs bg-gray-800 rounded-full">{availableCourses.length}</span>
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4">
              {accessType === 'training' ? (
                <div className="space-y-2">
                  {availableTrainings.map(training => {
                    const isSelected = selectedMaterials.trainings.includes(training.id);
                    const accessData = userMaterials.trainingsAccess[training.id];
                    const hasCustomPeriod = !!accessData;
                    
                    return (
                      <div
                        key={training.id}
                        className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                          isSelected
                            ? 'bg-purple-900/30 border-purple-700'
                            : 'bg-gray-800/50 border-gray-700 hover:bg-gray-800'
                        }`}
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-12 h-12 rounded overflow-hidden bg-gray-700 flex-shrink-0">
                            {training.image && (
                              <img src={training.image} alt={training.title} className="w-full h-full object-cover" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-white truncate">{training.title}</p>
                            <p className="text-xs text-gray-400 truncate">
                              {training.technique || 'Без техники'} • {training.lessons?.length || 0} уроков
                            </p>
                            {isSelected && hasCustomPeriod && (
                              <p className="text-xs text-purple-300 mt-1">
                                📅 {formatDisplayDate(accessData.startDate)} - {formatDisplayDate(accessData.endDate)}
                              </p>
                            )}
                            {isSelected && !hasCustomPeriod && globalStartDate && globalEndDate && (
                              <p className="text-xs text-gray-500 mt-1">
                                📅 {formatDisplayDate(globalStartDate)} - {formatDisplayDate(globalEndDate)} (по умолчанию)
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {isSelected && (
                            <button
                              onClick={() => startEditPeriod(training.id, 'training')}
                              className="p-1.5 text-blue-400 hover:text-blue-300 hover:bg-blue-900/30 rounded-lg transition-colors"
                              title="Редактировать период доступа"
                            >
                              <Settings className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => toggleTraining(training.id)}
                            className={`p-1.5 rounded-lg transition-colors ${
                              isSelected
                                ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                                : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                            }`}
                            title={isSelected ? 'Удалить доступ' : 'Добавить доступ'}
                          >
                            {isSelected ? <Trash className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  {availableTrainings.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      <BookOpen className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>Нет доступных тренингов</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {availableCourses.map(course => {
                    const isSelected = selectedMaterials.courses.includes(course.id);
                    const accessData = userMaterials.coursesAccess[course.id];
                    const hasCustomPeriod = !!accessData;
                    
                    return (
                      <div
                        key={course.id}
                        className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                          isSelected
                            ? 'bg-green-900/30 border-green-700'
                            : 'bg-gray-800/50 border-gray-700 hover:bg-gray-800'
                        }`}
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-12 h-12 rounded overflow-hidden bg-gray-700 flex-shrink-0">
                            {course.image && (
                              <img src={course.image} alt={course.title} className="w-full h-full object-cover" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-white truncate">{course.title}</p>
                            <p className="text-xs text-gray-400 truncate">
                              {course.blocks?.length || 0} блоков • {course.technique || 'Без техники'}
                            </p>
                            {isSelected && hasCustomPeriod && (
                              <p className="text-xs text-green-300 mt-1">
                                📅 {formatDisplayDate(accessData.startDate)} - {formatDisplayDate(accessData.endDate)}
                              </p>
                            )}
                            {isSelected && !hasCustomPeriod && globalStartDate && globalEndDate && (
                              <p className="text-xs text-gray-500 mt-1">
                                📅 {formatDisplayDate(globalStartDate)} - {formatDisplayDate(globalEndDate)} (по умолчанию)
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {isSelected && (
                            <button
                              onClick={() => startEditPeriod(course.id, 'course')}
                              className="p-1.5 text-blue-400 hover:text-blue-300 hover:bg-blue-900/30 rounded-lg transition-colors"
                              title="Редактировать период доступа"
                            >
                              <Settings className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => toggleCourse(course.id)}
                            className={`p-1.5 rounded-lg transition-colors ${
                              isSelected
                                ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                                : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                            }`}
                            title={isSelected ? 'Удалить доступ' : 'Добавить доступ'}
                          >
                            {isSelected ? <Trash className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  {availableCourses.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      <GraduationCap className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>Нет доступных курсов</p>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="border-t border-gray-700 p-4 flex gap-3">
              <button
                onClick={saveAllChanges}
                disabled={loading}
                className="flex-1 px-4 py-2.5 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {loading ? <><Loader className="w-4 h-4 animate-spin" />Сохранение...</> : <><Save className="w-4 h-4" />Сохранить все изменения</>}
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2.5 bg-gray-700 text-gray-300 font-medium rounded-lg hover:bg-gray-600 transition-colors"
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Модалка редактирования периода */}
      {editingMaterial && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-[60] backdrop-blur-sm">
          <div className="bg-gray-900 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl border border-gray-700">
            <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between bg-gradient-to-r from-gray-800 to-gray-900">
              <div className="flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-blue-400" />
                <h2 className="text-lg font-semibold text-white">Редактирование периода доступа</h2>
              </div>
              <button onClick={() => { setEditingMaterial(null); setEditingMaterialType(null); }} className="p-1 text-gray-400 hover:text-gray-300 hover:bg-gray-800 rounded transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-700">
                <p className="text-sm text-gray-400">Материал:</p>
                <p className="text-sm font-medium text-white">
                  {editingMaterialType === 'training' 
                    ? getTrainingTitle(editingMaterial)
                    : getCourseTitle(editingMaterial)}
                </p>
                <p className="text-xs text-gray-500 mt-1">ID: {editingMaterial}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Дата начала доступа</label>
                <input
                  type="date"
                  value={editStartDate}
                  onChange={(e) => setEditStartDate(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-800 text-white border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Дата окончания доступа</label>
                <input
                  type="date"
                  value={editEndDate}
                  onChange={(e) => setEditEndDate(e.target.value)}
                  min={editStartDate}
                  className="w-full px-4 py-2 bg-gray-800 text-white border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              {editStartDate && editEndDate && (
                <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-400">Длительность доступа:</span>
                    <span className="text-sm font-medium text-blue-300">
                      {getDaysBetween(editStartDate, editEndDate)} дней
                    </span>
                  </div>
                </div>
              )}
              
              <div className="flex gap-3 pt-4 border-t border-gray-700">
                <button
                  onClick={saveMaterialPeriod}
                  disabled={loading}
                  className="flex-1 px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {loading ? <><Loader className="w-4 h-4 animate-spin" />Сохранение...</> : <><Save className="w-4 h-4" />Сохранить</>}
                </button>
                <button
                  onClick={() => { setEditingMaterial(null); setEditingMaterialType(null); }}
                  className="px-4 py-3 bg-gray-700 text-gray-300 font-medium rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Отмена
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccessManager;